import React, { useState, useRef } from 'react';
import { UploadIcon, XIcon, FileIcon } from 'lucide-react';
export interface FileUploadProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSize?: number;
}
export function FileUpload({
  onFileSelect,
  accept,
  maxSize = 10485760
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const validateAndSelectFile = (file: File) => {
    setError('');
    if (maxSize && file.size > maxSize) {
      setError(`File size must be less than ${Math.round(maxSize / 1048576)}MB`);
      return;
    }
    setSelectedFile(file);
    onFileSelect(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSelectFile(file);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSelectFile(file);
    }
  };
  const handleRemove = () => {
    setSelectedFile(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };
  return <div className="w-full" data-id="element-350">
      {!selectedFile ? <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-400'}`} data-id="element-351">
          <UploadIcon className="w-12 h-12 mx-auto text-slate-400 mb-4" data-id="element-352" />
          <p className="text-sm text-slate-600 mb-1" data-id="element-353">
            <span className="font-medium text-slate-900" data-id="element-354">Click to upload</span>{' '}
            or drag and drop
          </p>
          <p className="text-xs text-slate-500" data-id="element-355">
            Maximum file size: {Math.round(maxSize / 1048576)}MB
          </p>
          <input ref={fileInputRef} type="file" onChange={handleFileChange} accept={accept} className="hidden" data-id="element-356" />
        </div> : <div className="border border-slate-300 rounded-lg p-4 flex items-center justify-between" data-id="element-357">
          <div className="flex items-center space-x-3" data-id="element-358">
            <FileIcon className="w-8 h-8 text-slate-400" data-id="element-359" />
            <div data-id="element-360">
              <p className="text-sm font-medium text-slate-900" data-id="element-361">
                {selectedFile.name}
              </p>
              <p className="text-xs text-slate-500" data-id="element-362">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </div>
          <button onClick={handleRemove} className="text-slate-400 hover:text-slate-600 transition-colors" aria-label="Remove file" data-id="element-363">
            <XIcon className="w-5 h-5" data-id="element-364" />
          </button>
        </div>}
      {error && <p className="mt-2 text-sm text-red-600" data-id="element-365">{error}</p>}
    </div>;
}